import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/api";
import { baseURL } from "@/api/utils/base-url";
import { getAuthHeaders } from "@/api/utils/headers";
import type { NetworxResponse } from "@/api/utils/types";
import { env } from "@/config";
import { MOCK_GUEST_USER } from "@/pages/homepage/mock/guest-user";
import { getMockUser } from "../complements/get-user-complements";

export function useMe() {
  return useQuery<MeUser>({
    queryKey: ["me"],
    queryFn: async () => {
      if (env.env === "LOCAL") {
        return getMockUser();
      }

      try {
        const response = await axiosInstance.get<MeResponse>("/me", {
          baseURL: baseURL.v2,
          headers: getAuthHeaders(),
          meta: {
            disableToasts: true,
          },
        });

        return response.data.response.return_data ?? MOCK_GUEST_USER;
      } catch {
        return MOCK_GUEST_USER;
      }
    },
  });
}

// Guest user (not logged in)
export type GuestUser = {
  isLoggedIn: false;
};

export type AccountLink = {
  id: string;
  label: string;
  url: string;
  icon?: string;
};

// Homeowner user
export type HomeownerUser = {
  isLoggedIn: true;
  userType: "homeowner";
  firstName: string;
  photoUrl?: string;
  accountLinks: AccountLink[];
};

// Contractor user
export type ContractorUser = {
  isLoggedIn: true;
  userType: "contractor";
  firstName: string;
  photoUrl: string | null;
  accountLinks: AccountLink[];
};

export type MeUser = GuestUser | HomeownerUser | ContractorUser;

type MeResponse = NetworxResponse<unknown, MeUser>;